"use client";

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { usePathname, useRouter } from 'next/navigation';
import { QueryClientProvider, QueryClient, useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { BrowserRouter, Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { useCompetitionContext } from "../providers/CompetitionContext";
import { RouteGuard } from './pages/RouteGuard';
import Points from './pages/Points';
import Leagues from './pages/Leagues';
import Calendar from './pages/Calendar';
import Ranking from './pages/Ranking';
import { CircleChevronLeft, CircleChevronRight } from 'lucide-react';
import { Match, Week } from '@repo/types';  
import { LoadingSpinner } from '../components/loading-spinner'
import moment from 'moment';
import Predictions from './pages/Predictions';
import Profile from './pages/Profile';
import UserPointsView from './pages/UserPointsView';

const queryClient = new QueryClient();

interface FantasyPageClientProps {
  user: {
    email?: string;
    username?: string;
    id?: string;  // This is now dbUserId (DynamoDB user ID) for prono games
    favClub?: {
      id: string | number;
      name: string;
    };
    isAdmin?: boolean;
  };
}

function RouteSyncer() {
  const pathname = usePathname();
  const navigate = useNavigate();
  const location = useLocation();
  const router = useRouter();

  useEffect(() => {
    // If navigating to /fantasy/home, use Next.js router for server-side rendering
    // This ensures the page is always rendered server-side with proper server actions
    if (pathname === '/fantasy/home') {
      // Only trigger if we're not already on /fantasy/home in React Router
      if (location.pathname !== '/fantasy/home') {
        console.log('[RouteSyncer] Navigating to /fantasy/home, using Next.js router for server-side rendering');
        router.push('/fantasy/home');
        return;
      }
      // If already on /fantasy/home, do nothing (Next.js will handle it)
      return;
    }
    
    // For other routes, sync React Router with Next.js pathname
    if (pathname && pathname !== location.pathname && pathname !== '/fantasy/home') {
      console.log('[RouteSyncer] External navigation detected, syncing React Router to:', pathname);
      navigate(pathname);
    }
  }, [pathname, location.pathname, navigate, router]);

  return null;
}

type UserRanksResponse = {
  generalRank: { rank: number; score: number } | null;
  weekRank: { rank: number; score: number } | null;
};

const fetchUserRanks = async (
  competitionFeed: string,
  seasonId: string | number,
  userId: string,
  weekId?: number
): Promise<UserRanksResponse> => {
  const params = new URLSearchParams({
    competitionFeed,
    seasonId: String(seasonId),
    userId,
  });

  if (typeof weekId === "number" && weekId >= 0) {
    params.set("weekId", String(weekId));
  }

  const response = await fetch(`/api/rankings/user?${params.toString()}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    return { generalRank: null, weekRank: null };
  }

  return response.json();
};

function MyPointsBanner({ userId }: { userId?: string }) {
  const { competitionConfig, weekId } = useCompetitionContext();

  const { data } = useQuery<UserRanksResponse>({
    queryKey: [
      "my-points-banner",
      competitionConfig?.competitionFeed,
      competitionConfig?.seasonId,
      userId,
      weekId,
    ],
    queryFn: () =>
      fetchUserRanks(
        competitionConfig.competitionFeed,
        competitionConfig.seasonId,
        userId!,
        weekId
      ),
    enabled: Boolean(userId && competitionConfig?.competitionFeed && competitionConfig?.seasonId),
    staleTime: 30_000,
  });

  const totalPoints = data?.generalRank?.score ?? 0;
  const dailyRank = data?.weekRank?.rank ?? "-";
  const overallPoints = data?.weekRank?.score ?? 0;
  const overallRank = data?.generalRank?.rank ?? "-";

  return (
    <div className="mx-auto mt-4 max-w-6xl px-4 md:px-0">
      <p className="mb-2 text-[20px] font-semibold text-white">Mes points</p>
      <div className="flex items-center justify-center">
        <div className="flex items-center">
          <div className="flex h-[96px] flex-col items-center justify-center px-4">
            <div className="flex h-[72px] w-[72px] flex-col items-center justify-center rounded-[4px] bg-[var(--rtbf-orange,#fdc300)] text-[var(--rtbf-dark,#001425)]">
              <p className="text-[24px] leading-none">{totalPoints}</p>
              <p className="text-[12px] leading-none">points</p>
            </div>
          </div>
          <div className="flex w-[185px] flex-col gap-1 text-[12px] leading-none">
            <div className="flex h-[21px] items-center justify-between rounded-[4px] bg-white px-2 py-1">
              <span className="text-[rgba(0,0,0,0.65)]">Classement du jour</span>
              <span className="font-medium text-[var(--rtbf-blue-dark,#003e63)]">{dailyRank}</span>
            </div>
            <div className="flex h-[21px] items-center justify-between rounded-[4px] bg-[var(--light,#f0f5f9)] px-2 py-1">
              <span className="text-[rgba(0,0,0,0.65)]">Points totaux</span>
              <span className="font-medium text-[var(--rtbf-blue-dark,#003e63)]">{overallPoints}</span>
            </div>
            <div className="flex h-[21px] items-center justify-between rounded-[4px] bg-white px-2 py-1">
              <span className="text-[rgba(0,0,0,0.65)]">Classement général</span>
              <span className="font-medium text-[var(--rtbf-blue-dark,#003e63)]">{overallRank}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LayoutWithWeekSwitch({ user }: { user: FantasyPageClientProps["user"] }) {
  return (
    <>
    <Outlet />
    </>
  );
}

export function FantasyPageClient({ user }: FantasyPageClientProps) {
  const [isClient, setIsClient] = useState(false);
  const { competitionData, weekId } = useCompetitionContext();

  useEffect(() => {
    console.log('[FantasyPageClient] Component mounted with user:', {
      userId: user?.id,
      email: user?.email,
      username: user?.username,
      weekId,
      hasCompetitionData: !!competitionData
    });
  }, [user, weekId, competitionData]);

  useEffect(() => {
    if (!user?.email) {
      console.log('[FantasyPageClient] No user email, skipping Crisp setup');
      return;
    }
    
    console.log('[FantasyPageClient] Setting up Crisp for user:', user.email);
    const interval = setInterval(() => {
      if (window.$crisp && typeof window.$crisp.push === 'function') {
        console.log('[FantasyPageClient] Crisp available, setting user data');
        window.$crisp.push(["set", "user:email", [user.email]]);
        window.$crisp.push(["set", "user:nickname", [user.username]]);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    console.log('[FantasyPageClient] Setting isClient to true');
    setIsClient(true);
  }, []);

  console.log('[FantasyPageClient] Rendering, isClient:', isClient);

  return (
    <>
      {isClient ? (
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <RouteSyncer />
            <RouteGuard user={user}>
                <Routes>
                  <Route path="/fantasy" element={<LayoutWithWeekSwitch user={user} />}>
                    <Route path="predictions" element={<Predictions weekId={weekId} user={user} />} />
                    <Route path="leagues" element={<Leagues weekId={weekId} user={user} />} />
                    <Route path="profile" element={<Profile user={user} />} />
                    {/* <Route path="calendar" element={<Calendar />} /> */}
                    <Route path="rankings" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    <Route path="rankings/:tab" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    <Route path="rankings/:tab/:weekId" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    <Route path="rankings/:tab/:weekId/:pageNumber" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    <Route path="rankings/:tab/:weekId/:pageNumber/:pageSize" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    <Route path="rankings/:tab/:weekId/:pageNumber/:pageSize/:searchName" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    <Route path="rankings/:tab/:weekId/:pageNumber/:pageSize/:searchName/:userId" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    <Route path="rankings/:tab/:weekId/:pageNumber/:pageSize/:searchName/:userId/:selectedWeekId" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    <Route path="rankings/:tab/:weekId/:pageNumber/:pageSize/:searchName/:userId/:selectedWeekId/:selectedTitle" element={<Ranking userId={user.id} isAdmin={user.isAdmin} />} />
                    {/* Admin-only route to view any user's predictions */}
                    <Route path="user-points/:targetUserId" element={<UserPointsView weekId={weekId} isAdmin={user.isAdmin} />} />
                  </Route>
                  {/* <Route path="*" element={<Navigate to="/" replace />} /> */}
                  {/* <Route path="/*" element={<div>404 Not Found</div>} /> */}
                </Routes>
            </RouteGuard>
          </BrowserRouter>
        </QueryClientProvider>
      ) : (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div role="status">
              <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[var(--bet-red)]" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

